import Axios, {
  AxiosResponse,
  HttpStatusCode,
  InternalAxiosRequestConfig,
} from 'axios';
import { getPersistedState } from './persist-util';
import { ROUTE_API } from './route-util';
import eventBus from './eventBus';

// Main Axios instance for API calls
const HttpUtil = Axios.create({
  baseURL: process.env.REACT_APP_API_URL,
});

// Separate Axios instance for refresh token calls to avoid interceptor interference
const RefreshHttpUtil = Axios.create({
  baseURL: process.env.REACT_APP_API_URL,
});

// Request interceptor to attach token and set headers
HttpUtil.interceptors.request.use(
  async (config: InternalAxiosRequestConfig<any>) => {
    const persistedState =
      getPersistedState(process.env.REACT_APP_PERSIST_AUTH) || {};
    const token = persistedState?.token;
    if (token) {
      config.headers.set('Authorization', `Bearer ${token}`);
    }
    if (config.url?.includes(ROUTE_API.uploadImage)) {
      config.headers.set('Content-Type', 'multipart/form-data');
    } else {
      config.headers.set('Content-Type', 'application/json');
    }
    config.headers.set('Accept', '*/*');
    return config;
  }
);

// Response interceptor
HttpUtil.interceptors.response.use(
  async function (res: AxiosResponse) {
    // Check if response exists and has data
    if (!res?.data) {
      return Promise.reject({ message: 'No data received from server' });
    }

    // If the response has an error property, check its value
    if ('error' in res.data) {
      if (res.data.error === 0) {
        return res.data; // Success case
      }
      // If there's an error message in the response, use it
      return Promise.reject({
        message: res.data.message || 'Encounter a problem signing in',
        ...res.data,
      });
    }

    // If no error property, assume success
    return res.data;
  },
  async function (error) {
    const originalRequest = error.config;

    // Prevent infinite loop by checking if we've already retried
    if (
      error.response?.status === HttpStatusCode.Unauthorized &&
      !originalRequest._retry
    ) {
      originalRequest._retry = true; // Mark as retried

      const persistedState =
        getPersistedState(process.env.REACT_APP_PERSIST_AUTH) || {};
      const refreshToken = persistedState?.refreshToken;

      if (!refreshToken) {
        return Promise.reject(error.response?.data);
      }

      try {
        // Attempt to refresh the token
        const response = await runRefreshToken(refreshToken);

        if (!response || response.status_code !== HttpStatusCode.Ok) {
          throw new Error('Refresh token failed');
        }

        // Update persisted state with new tokens
        const newState = {
          ...persistedState,
          token: response.accessToken,
          refreshToken: response.refreshToken || refreshToken,
        };
        localStorage.setItem(
          process.env.REACT_APP_PERSIST_AUTH || '',
          JSON.stringify(newState)
        );

        // Update the original request with the new token
        originalRequest.headers[
          'Authorization'
        ] = `Bearer ${response.accessToken}`;

        // Retry the original request
        return HttpUtil(originalRequest);
      } catch (refreshError) {
        eventBus.emit('auth:login', {
          message: 'Session has expired. Please log in again.',
        });
        return Promise.reject(error.response?.data || refreshError);
      }
    }

    // For other errors or after retry, reject the promise
    return Promise.reject(error.response?.data || error);
  }
);

// Function to refresh token using a separate Axios instance
async function runRefreshToken(token: string) {
  try {
    const res = await RefreshHttpUtil.post(ROUTE_API.refreshToken, { token });
    return res.data; // Expecting { accessToken, refreshToken, status_code }
  } catch (e) {
    console.error('Refresh token request failed:', e);
    return null;
  }
}

export default HttpUtil;
